import { AppError } from 'components/AppError';
import BulkUploadsPolling from 'components/BulkUploadsPolling';
import {
  BulkUploadStatus,
  BulkUploadsValidationPollStatuses,
} from 'constants/bulkUpload';
import React, { ReactElement, useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import api from 'api';
import { BulkUploadDetailsSchemaContract } from 'schemas/BulkUploadDetailsSchema';
import { FileUploadStatus, MimeType } from 'components/ui/FileUpload/constants';
import { ErrorAlert } from 'components/BulkUploads/BladeComponents/ErrorAlert';
import { getStatusBasedOnBulkRecord } from 'components/BulkUploads/Utils/utils';
import { truncateString } from 'utils/Strings';
import { StepHeader } from 'components/WizardViews/components';
import { Box } from '@razorpay/blade/components';
import { BulkFileUpload } from 'components/ui/FileUpload/BulkFileUpload';
import { useBulkUploadType } from '../hooks/useBulkUploadType';

type FileUploadSectionProps = {
  bulkUploadRecord: BulkUploadDetailsSchemaContract[0] | null;
  setBulkUploadRecord: (bulkUploadRecord: BulkUploadDetailsSchemaContract[0] | null) => void;
  onChangeFile?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onIncorrectFileTypeSelection?: () => void;
  onExceededSizeSelection?: () => void;
  onDownloadErrorReport?: () => void;
  title: string;
  pollingRequestId: string;
};

export const FileUploadSection = ({
  bulkUploadRecord,
  setBulkUploadRecord,
  onChangeFile,
  onIncorrectFileTypeSelection,
  onExceededSizeSelection,
  onDownloadErrorReport,
  title,
  pollingRequestId,
}: FileUploadSectionProps): ReactElement => {
  const bulkUploadType = useBulkUploadType();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileUploadCancelled, setFileUploadCancelled] = useState<Boolean>(false);
  const [status, setStatus] = useState<FileUploadStatus>(FileUploadStatus.NOT_INITIATED);
  const [error, setError] = useState<string>('');

  const cancelBulkUpload = useMutation({
    mutationFn: api.bulkUploads.cancelBulkUpload,
    onSuccess: () => {
      setBulkUploadRecord(null);
    },
    onError: (error: typeof AppError) => {
      console.error("Error on cancelling org's bulk upload : ", error);
    },
  });

  const sendFile = useMutation(
    (requestData: FormData) => {
      if (requestData && requestData.has('type') && requestData.has('file')) {
        return api.bulkUploads.sendFile(requestData);
      } else {
        throw 'Could not upload your file.';
      }
    },
    {
      onSuccess: (data) => {
        setBulkUploadRecord(data.length > 0 ? data[0] : null);

        sessionStorage.setItem(pollingRequestId, data[0].id.toString());

        if (data.length > 0 && data[0].status === BulkUploadStatus.STATUS_VALIDATION_FAILED) {
          setStatus(FileUploadStatus.FAILURE);
        } else if (
          data.length > 0 &&
          data[0].status === BulkUploadStatus.STATUS_VALIDATION_SUCCESS
        ) {
          setStatus(FileUploadStatus.SUCCESS);
        }
      },
      onError: (error: typeof AppError) => {
        setError(error.toString());
        setStatus(FileUploadStatus.FAILURE);
      },
    },
  );

  const onCancel = () => {
    setFileUploadCancelled(true);
    sessionStorage.removeItem(pollingRequestId);

    if (bulkUploadRecord) {
      cancelBulkUpload.mutate(bulkUploadRecord.id);
    }
  };

  const shouldPoll = () => {
    if (
      bulkUploadRecord?.id &&
      sessionStorage.getItem(pollingRequestId) &&
      !fileUploadCancelled &&
      BulkUploadsValidationPollStatuses.includes(bulkUploadRecord.status)
    ) {
      return true;
    }
    return false;
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(event.target.files ? event.target.files[0] : null);
    setFileUploadCancelled(false);
    setStatus(FileUploadStatus.UPLOADING);

    onChangeFile?.(event);

    if (event.target.files && event.target.files[0].type !== MimeType.XLSX) {
      setStatus(FileUploadStatus.FAILURE);
      setError('File uploaded is not of type .xlsx');
      onIncorrectFileTypeSelection?.();

      return;
    }

    if (event.target.files && event.target.files[0].size / 1048576 > 5) {
      setStatus(FileUploadStatus.FAILURE);
      setError('File uploaded is greater than 5 MB');
      onExceededSizeSelection?.();

      return;
    }

    let requestData = new FormData();
    requestData.append('type', bulkUploadType.toString());
    requestData.append('file', event.target.files ? event.target.files[0] : '');
    sendFile.mutate(requestData);
  }

  useEffect(() => {
    setStatus(getStatusBasedOnBulkRecord(bulkUploadRecord));

    if (
      !bulkUploadRecord?.error_file_url &&
      bulkUploadRecord?.errors &&
      bulkUploadRecord.errors.length > 0
    ) {
      setError(bulkUploadRecord?.errors[0]);
    }
  }, [bulkUploadRecord]);

  return (
    <>
      {shouldPoll() && (
        <BulkUploadsPolling
          id={String(bulkUploadRecord?.id)}
          action={'file-upload'}
          setBulkUploadRecordData={setBulkUploadRecord}
        />
      )}
      <StepHeader marginBottom="spacing.5" subtitle={title} title="" />
      <Box>
        <BulkFileUpload
          id={'bulk-upload-input-file'}
          onChange={onChange}
          onCancel={onCancel}
          status={status}
          value={truncateString(
            selectedFile
              ? selectedFile.name
              : bulkUploadRecord?.file_name
              ? bulkUploadRecord?.file_name
              : 'File Not Found',
            25,
          )}
          allowedMimeTypes={[MimeType.XLSX]}
          maxSize={5}
          error={bulkUploadRecord && bulkUploadRecord.error_file_url ? '' : error}
          disableFileReuploadButton={[
            BulkUploadStatus.STATUS_VALIDATION_PENDING,
            BulkUploadStatus.STATUS_VALIDATION_PROGRESS,
          ].includes(bulkUploadRecord?.status ?? 0)}
          disableCancelButton={!shouldPoll()}
        />
      </Box>
      <Box>
        {bulkUploadRecord &&
          bulkUploadRecord.status === BulkUploadStatus.STATUS_VALIDATION_FAILED &&
          bulkUploadRecord.error_file_url && (
            <Box>
              <ErrorAlert
                mainText={
                  bulkUploadRecord.number_of_rows_with_error?.toString() + ' errors found'
                }
                subText={'Please download error report > fix errors > re-upload file '}
                buttonText={'Download Report'}
                downloadURL={bulkUploadRecord.error_file_url ?? ''}
                onClick={onDownloadErrorReport}
              />
            </Box>
          )}
      </Box>
    </>
  );
}
